import { Observable } from "rxjs";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    DEFAULT_CANVAS_HEIGHT,
    DEFAULT_CANVAS_WIDTH,
    DEFAULT_IMAGE_HEIGHT,
    DEFAULT_IMAGE_WIDTH,
    DEFAULT_POSITION_X,
    DEFAULT_POSITION_Y,
} from "./constants";
import { ImageOptions, TextOptions } from "./types";

class CanvasManager {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D | null;

    constructor(private logger?: LoggerInterface) {
        logger?.debug("CanvasManager initialized");
        this.canvas = document.createElement("canvas");
        this.ctx = this.canvas.getContext("2d");
        this.setCanvasSize(DEFAULT_CANVAS_WIDTH, DEFAULT_CANVAS_HEIGHT);
        if (!this.ctx) {
            throw new Error("Unable to get canvas context");
        }
    }

    public setCanvasSize(canvasWidth: number, canvasHeight: number): void {
        if (!this.ctx) return;
        const ratio = window.devicePixelRatio;

        this.canvas.width = canvasWidth * ratio;
        this.canvas.height = canvasHeight * ratio;
        this.canvas.style.width = `${canvasWidth}px`;
        this.canvas.style.height = `${canvasHeight}px`;
        this.ctx.scale(ratio, ratio);
        this.ctx.fillStyle = "white";
        this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    public drawImage(
        imageUrl: string,
        options: ImageOptions
    ): Observable<void> {
        return new Observable<void>((subscriber) => {
            const {
                positionX = DEFAULT_POSITION_X,
                positionY = DEFAULT_POSITION_Y,
                width = DEFAULT_IMAGE_WIDTH,
                height = DEFAULT_IMAGE_HEIGHT,
                backgroundColor = "transparent",
                padding = 0,
            } = options;

            const image = new Image();
            image.src = imageUrl;

            image.onload = () => {
                if (!this.ctx) return;

                this.ctx.fillStyle = backgroundColor;
                this.ctx.fillRect(positionX, positionY, width, height);

                const imgAspectRatio = image.width / image.height;
                const rectAspectRatio = width / height;

                let drawWidth: number,
                    drawHeight: number,
                    offsetX: number,
                    offsetY: number;

                if (imgAspectRatio > rectAspectRatio) {
                    drawWidth = width - padding * 2;
                    drawHeight = drawWidth / imgAspectRatio;
                    offsetX = positionX + padding;
                    offsetY = positionY + (height - drawHeight) / 2;
                } else {
                    drawHeight = height - padding * 2;
                    drawWidth = drawHeight * imgAspectRatio;
                    offsetX = positionX + (width - drawWidth) / 2;
                    offsetY = positionY + padding;
                }

                this.ctx.drawImage(
                    image,
                    offsetX,
                    offsetY,
                    drawWidth,
                    drawHeight
                );

                subscriber.next();
                subscriber.complete();
            };

            image.onerror = () => {
                this.logger?.debug("Failed to load image");
                subscriber.error(new Error("Failed to load image"));
            };
        });
    }

    public drawText(text: string, options: TextOptions): Observable<void> {
        return new Observable((observer) => {
            const {
                fontSize = 12,
                fontFamily = "Arial",
                textColor = "black",
                positionX = DEFAULT_POSITION_X,
                positionY = DEFAULT_POSITION_Y,
                textAlign = "left",
            } = options;

            if (!this.ctx) return;
            this.ctx.textAlign = textAlign;
            this.ctx.font = `${fontSize}px ${fontFamily}`;
            this.ctx.fillStyle = textColor;
            this.ctx.fillText(text, positionX, positionY);

            observer.next();
            observer.complete();
        });
    }

    public clearCanvas(backgroundColor = "white"): void {
        if (!this.ctx) return;
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        this.ctx.fillStyle = backgroundColor;
        this.ctx.fillRect(
            0,
            0,
            this.canvas.width / window.devicePixelRatio,
            this.canvas.height / window.devicePixelRatio
        );
    }

    public getMeasureTextWidth(text: string, fontSize = 12) {
        if (!this.ctx) return 0;
        this.ctx.font = `${fontSize}px Arial`;
        return this.ctx.measureText(text).width;
    }

    public selectDrawingImageDataUrl(): string {
        return this.canvas.toDataURL("image/png");
    }
}

export default CanvasManager;
